<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';

import FormSelect from 'v3/components/Form/Select.vue';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const isUpdating = ref(false);
// Re-renders the select so it shows the saved language again after a failed save.
const selectKey = ref(0);

const currentLanguage = computed(() => uiSettings.value?.locale ?? '');

const languageOptions = computed(() => [
  {
    name: t(
      'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.USE_ACCOUNT_DEFAULT'
    ),
    iso_639_1_code: '',
  },
  ...(enabledLanguages ?? []),
]);

const updateLanguage = async languageCode => {
  if (isUpdating.value) return;
  isUpdating.value = true;
  try {
    const valid =
      !languageCode ||
      (enabledLanguages || []).some(l => l.iso_639_1_code === languageCode);
    if (!valid) {
      throw new Error(`Invalid language code: ${languageCode}`);
    }

    const saved = await updateUISettings(
      { locale: languageCode || null },
      { persistBeforeApply: true }
    );
    if (!saved) throw new Error('Language preference could not be saved');

    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
    );
  } catch (error) {
    selectKey.value += 1;
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
  } finally {
    isUpdating.value = false;
  }
};

const selectedValue = computed({
  get: () => currentLanguage.value,
  set: value => {
    updateLanguage(value);
  },
});
</script>

<template>
  <div class="flex gap-2 justify-between w-full items-start">
    <div>
      <label
        for="preferred-language"
        class="text-n-gray-12 font-medium leading-6 text-sm"
      >
        {{ label }}
      </label>
      <p class="text-n-gray-11">
        {{ description }}
      </p>
    </div>
    <FormSelect
      id="preferred-language"
      :key="selectKey"
      v-model="selectedValue"
      name="language"
      spacing="compact"
      class="min-w-28 mt-px"
      :options="languageOptions"
      :disabled="isUpdating"
      :show-placeholder="false"
      label=""
    >
      <option
        v-for="option in languageOptions"
        :key="option.iso_639_1_code || 'default'"
        :value="option.iso_639_1_code"
        :selected="option.iso_639_1_code === selectedValue"
      >
        {{ option.name }}
      </option>
    </FormSelect>
  </div>
</template>
